/**
 * One Thẻ Chỉ Số: an 11px key, one figure, and the qualifier that keeps the figure
 * honest.
 *
 * ── The world's metric card, not a paragraph with a number in it ────────────
 *
 * The sibling product's `DESIGN.md` specifies it exactly — key 11px caps muted,
 * value 26px/700 with `tabular-nums` and −0.6px tracking, radius 10, and a card that
 * **needs action** tinted on all four sides rather than striped down one. The finish
 * review found this file rendering three stacked paragraphs of caveat under every
 * figure instead, which is how "Ba con số của ngày" became a column of prose with
 * three numbers hidden in it.
 *
 * So: the figure and at most one line under it stay on the paper; everything else
 * goes behind `<details>`. Nothing is deleted — `DESIGN.md` §8's rule still holds
 * (*"Con số attendance không kèm completeness là con số nói dối"*) — but a
 * completeness note two clauses long is a footnote, and a footnote printed at the
 * same weight as the figure buries the figure.
 *
 * ── `attention` opens the footnote as well as tinting the card ──────────────
 *
 * A disclosure that hides a delay warning would be worse than the paragraphs it
 * replaced. So the one flag does both jobs: a tile whose figure needs action is
 * tinted **and** has its qualifier already open. Colour is still the third channel —
 * the open disclosure and the words in it are the first two.
 *
 * `figure` is a `ReactNode` rather than a `number` on purpose. Several tiles have a
 * state in which **there is no figure**, and a component that took a number would
 * have to be handed a `0` to render that state.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';

export interface TileProps {
  readonly title: string;
  /**
   * Keeps the key for a screen reader and takes it off the paper. For the one tile
   * whose figure is a sentence that already says what the key says — a caps label
   * directly above a heading is the eyebrow the finish review deleted.
   */
  readonly titleHidden?: boolean;
  /** The number, or the state that stands in place of one. Never a stand-in zero. */
  readonly figure: ReactNode;
  /** One line: the window, the denominator, the sample. */
  readonly detail?: ReactNode;
  /** Completeness, delay and refusal notes. Behind the disclosure, open when it matters. */
  readonly caveat?: ReactNode;
  /**
   * The figure needs somebody to do something: tinted card, open qualifier.
   *
   * Not "the figure is missing". A tile with no number is one of the nine states and
   * rests on plain paper like every other tile — colour that marks an absence is
   * colour without a meaning (Luật Màu Có Nghĩa), and when all three figures are
   * absent it tints the whole band (finish review, round 2, fix 2).
   */
  readonly attention?: boolean;
  readonly testId: string;
}

export function Tile({
  title,
  titleHidden = false,
  figure,
  detail,
  caveat,
  attention = false,
  testId,
}: TileProps): ReactNode {
  const { t } = useI18n();
  return (
    <section
      className={attention ? 'wp-tile wp-tile--attention' : 'wp-tile'}
      data-testid={testId}
      aria-labelledby={`${testId}-title`}
    >
      <h3
        className={titleHidden ? 'wp-tile__title wp-visually-hidden' : 'wp-tile__title'}
        id={`${testId}-title`}
      >
        {title}
      </h3>
      <div className="wp-tile__figure" data-testid={`${testId}-figure`}>
        {figure}
      </div>
      {detail === undefined ? null : (
        <div className="wp-tile__detail" data-testid={`${testId}-detail`}>
          {detail}
        </div>
      )}
      {caveat === undefined ? null : (
        <details className="wp-tile__more wp-disclosure" open={attention}>
          <summary className="wp-disclosure__summary">{t('common.moreDetail')}</summary>
          <div className="wp-tile__caveat" data-testid={`${testId}-caveat`}>
            {caveat}
          </div>
        </details>
      )}
    </section>
  );
}
